import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Hero from "./components/Hero";
import PreviewSection from "./components/PreviewSection";
import Curriculum from "./components/Curriculum";
import InteractiveScenario from "./components/InteractiveScenario";
import Quiz from "./components/Quiz";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import CTA from "./components/CTA";
import Footer from "./components/Footer";
import LeadCapture from "./components/LeadCapture";
import AuthCallback from "./components/AuthCallback";
import ProgressDashboard from "./components/ProgressDashboard";
import CertificateDisplay from "./components/CertificateDisplay";
import CertificateVerify from "./components/CertificateVerify";
import WhySafetyTraining from "./components/WhySafetyTraining";
import Instructors from "./components/Instructors";
import SocialProof from "./components/SocialProof";
import StickyCTA from "./components/StickyCTA";
import CourseFeatures from "./components/CourseFeatures";
import ComparisonTable from "./components/ComparisonTable";
import CourseRoadmap from "./components/CourseRoadmap";
import DownloadableResources from "./components/DownloadableResources";
import FloatingHelp from "./components/FloatingHelp";
import RecentEnrollment from "./components/RecentEnrollment";
import ScrollProgress from "./components/ScrollProgress";
import BackToTop from "./components/BackToTop";
import TrustBadges from "./components/TrustBadges";
import Guarantee from "./components/Guarantee";
import ModulePage from "./components/ModulePage";
import AdminDashboard from "./components/AdminDashboard";
import UserDashboard from "./components/UserDashboard";
import SEO, { FAQSchema } from "./components/SEO";
import { toast } from "sonner";
import { getCurrentUser, logout } from "./services/api";

// Main course page component
const CoursePage = () => {
  const location = useLocation();
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [leadType, setLeadType] = useState('start');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(1);

  useEffect(() => {
    // Check if user data was passed from AuthCallback
    if (location.state?.user) {
      setCurrentUser(location.state.user);
      setIsEnrolled(true);
      setIsLoading(false);
      
      if (location.state.lead) {
        setLeadData(location.state.lead);
        localStorage.setItem('oshaLeadId', location.state.lead.id);
        localStorage.setItem('oshaLeadData', JSON.stringify(location.state.lead));
      }
      
      toast.success(`Welcome ${location.state.user.name}! Your free OSHA training is ready.`);
      
      // Clear location state
      window.history.replaceState({}, document.title);
      
      // Scroll to progress dashboard
      setTimeout(() => {
        document.getElementById('progress-dashboard')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      return;
    }

    // Check existing auth session
    const checkAuth = async () => {
      try {
        const data = await getCurrentUser();
        if (data?.user) {
          setCurrentUser(data.user);
          setIsEnrolled(true);
          
          if (data.lead) {
            setLeadData(data.lead);
            localStorage.setItem('oshaLeadId', data.lead.id);
            localStorage.setItem('oshaLeadData', JSON.stringify(data.lead));
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.state]);

  const handleStartCourse = () => {
    if (isEnrolled) {
      document.getElementById('progress-dashboard')?.scrollIntoView({ behavior: 'smooth' });
      toast.success(`Welcome back${currentUser?.name ? ', ' + currentUser.name.split(' ')[0] : ''}! Continue your training below.`);
    } else {
      setLeadType('start');
      setShowLeadCapture(true);
    }
  };

  const handleEnroll = () => {
    if (isEnrolled) {
      document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setLeadType('start');
      setShowLeadCapture(true);
    }
  };

  const handleLeadSubmit = (userData) => {
    setIsEnrolled(true);
    setCurrentUser(userData);
    toast.success(`Welcome ${userData.name}! Your free OSHA training is ready.`);
    setTimeout(() => {
      document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  };

  const handleWatchPreview = () => {
    document.getElementById('preview')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogout = async () => {
    try {
      await logout();
      setCurrentUser(null);
      setIsEnrolled(false);
      setLeadData(null);
      localStorage.removeItem('oshaLeadId');
      localStorage.removeItem('oshaLeadData');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleGetCertificate = () => {
    if (isEnrolled) {
      setShowCertificate(true);
    } else {
      setLeadType('certificate');
      setShowLeadCapture(true);
    }
  };

  const handleModuleSelect = (moduleId) => {
    setSelectedModule(moduleId);
    setTimeout(() => {
      document.getElementById('quiz')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const leadId = leadData?.id || localStorage.getItem('oshaLeadId');

  return (
    <>
      <Header 
        onStartCourse={handleStartCourse} 
        currentUser={currentUser}
        onLogout={handleLogout}
        onGetCertificate={handleGetCertificate}
      />
      
      <main>
        <Hero 
          onStartCourse={handleStartCourse} 
          onWatchPreview={handleWatchPreview}
        />
        
        <SocialProof />
        
        <TrustBadges />
        
        {/* Progress Dashboard - Only show for enrolled users */}
        {isEnrolled && (
          <div id="progress-dashboard">
            <ProgressDashboard 
              leadId={leadId}
              userName={currentUser?.name}
              onModuleSelect={handleModuleSelect}
            />
          </div>
        )}
        
        <div id="preview">
          <PreviewSection />
        </div>
        
        <WhySafetyTraining />
        
        <ComparisonTable onEnroll={handleEnroll} />
        
        <div id="curriculum">
          <Curriculum onModuleSelect={handleModuleSelect} />
        </div>
        
        <CourseRoadmap />
        
        <div id="scenarios">
          <InteractiveScenario />
        </div>
        
        <div id="quiz">
          <Quiz 
            moduleId={selectedModule} 
            onModuleChange={setSelectedModule}
            isEnrolled={isEnrolled}
          />
        </div>
        
        <Testimonials />
        
        <Instructors />
        
        <CourseFeatures />
        
        <DownloadableResources onEnroll={handleEnroll} isEnrolled={isEnrolled} />
        
        <Guarantee onEnroll={handleEnroll} />
        
        <div id="faq">
          <FAQ />
        </div>
        
        <CTA onEnroll={handleEnroll} />
      </main>
      
      <Footer />
      
      <StickyCTA onEnroll={handleEnroll} isEnrolled={isEnrolled} />
      
      <FloatingHelp />
      
      <RecentEnrollment />
      
      <BackToTop />
      
      <ScrollProgress />
      
      <LeadCapture 
        isOpen={showLeadCapture}
        onClose={() => setShowLeadCapture(false)}
        onSubmit={handleLeadSubmit}
        type={leadType}
      />
      
      <CertificateDisplay 
        isOpen={showCertificate}
        onClose={() => setShowCertificate(false)}
        leadId={leadId}
        userName={currentUser?.name}
        userEmail={currentUser?.email}
      />
    </>
  );
};

// App Router with auth callback handling
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (auth callback) - MUST be synchronous
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<CoursePage />} />
      <Route path="/module/:moduleId" element={<ModulePage />} />
      <Route path="/dashboard" element={<UserDashboardPage />} />
      <Route path="/verify" element={<CertificateVerify />} />
      <Route path="/verify/:certNumber" element={<CertificateVerify />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="*" element={<CoursePage />} />
    </Routes>
  );
};

// User Dashboard Page wrapper to get user context
const UserDashboardPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Not logged in');
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <UserDashboard 
      userId={user?.lead?.id || user?.user?.user_id || localStorage.getItem('oshaLeadId')}
      userName={user?.user?.name}
      userEmail={user?.user?.email}
    />
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-white">
        <SEO />
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
